import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import {
  BodyS,
  BrandXXL,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { AnyRewardConfig } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { isType } from '@sorare/core/src/lib/gql';

import type { CollectionCompletionDetailsDialog_taskInterface } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--double-unit);
`;
const Thresholds = styled(Vertical).attrs({ gap: 2 })`
  overflow: hidden;
  padding: var(--triple-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
`;
const Threshold = styled(Vertical).attrs({ gap: 1.5 })`
  &.disabled {
    opacity: 0.5;
  }
`;
const CollectionScoreWrapper = styled.div`
  position: relative;
  isolation: isolate;
`;
const CollectionScore = styled(BrandXXL)`
  text-shadow: 0 0 8px var(--c-nd-500);
`;
const AbsoluteScarcityIcon = styled(ScarcityIcon).attrs({ size: 4 })`
  position: absolute;
  top: 50%;
  right: calc(-1 * var(--double-unit));
  z-index: -1;
  transform: translateY(-50%) rotate(15deg);
`;

type Props = {
  open?: boolean;
  onClose?: () => void;
  task: CollectionCompletionDetailsDialog_taskInterface;
};
export const CollectionCompletionDetailsDialog = ({
  open,
  onClose,
  task,
}: Props) => {
  if (!isType(task, 'CompleteCollectionsCountTask')) {
    return null;
  }

  const {
    title,
    iconUrl,
    thresholds,
    liveScore: collectionsCompleted,
    userCollectionClosestToCompletion,
  } = task;

  const currentThresholdIndex = thresholds.findIndex(
    ({ current }) => !!current
  );

  const { fulfilledSlotsCount, slotsCount, cardCollection } =
    userCollectionClosestToCompletion || {};
  const userCollectionClosestToCompletionProgress =
    (fulfilledSlotsCount || 0) / (slotsCount || 1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        <Horizontal center>
          <LabelL bold>{title}</LabelL>
          {!!iconUrl && <img src={iconUrl} width={24} height={24} alt="" />}
        </Horizontal>
      }
      maxWidth="sm"
    >
      <Root>
        <Vertical gap={0.5} center>
          <CollectionScoreWrapper>
            <CollectionScore>{collectionsCompleted}</CollectionScore>
            <AbsoluteScarcityIcon rarity={Rarity.common} />
          </CollectionScoreWrapper>
          <LabelM>
            <FormattedMessage defaultMessage="Collections completed" />
          </LabelM>
        </Vertical>
        <Thresholds>
          <Vertical>
            <SBHorizontal>
              {!!cardCollection && (
                <Horizontal>
                  <img
                    src={cardCollection.anyTeam?.pictureUrl || ''}
                    width={24}
                    alt=""
                  />
                  <LabelS bold color="var(--c-nd-600)">
                    {cardCollection.anyTeam?.name}
                  </LabelS>
                </Horizontal>
              )}
              <LabelS bold>
                <FormattedNumber
                  value={userCollectionClosestToCompletionProgress}
                  style="percent"
                />
              </LabelS>
            </SBHorizontal>
            <Progress
              value={userCollectionClosestToCompletionProgress}
              max={thresholds[currentThresholdIndex]?.count}
              min={0}
              color="linear-gradient(to right, var(--c-yellow-500), var(--c-yellow-300))"
              bgColor="var(--c-nd-200)"
            />
          </Vertical>
          <Scrollable itemToDisplay={5} indexToScroll={currentThresholdIndex}>
            {thresholds.map(({ count, current, rewardConfigs }, i) => {
              const thresholdValidated = i < currentThresholdIndex;
              const [rewardConfig] = rewardConfigs;
              return (
                <Threshold
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  center
                  className={classNames({ disabled: !!thresholdValidated })}
                >
                  <AnyRewardConfig anyRewardConfig={rewardConfig} />
                  <Horizontal gap={0.5}>
                    {thresholdValidated ? (
                      <FontAwesomeIcon
                        icon={iconCheck}
                        color="var(--c-onBase-success)"
                      />
                    ) : (
                      <Collection />
                    )}
                    <LabelS bold={current}>{count}</LabelS>
                  </Horizontal>
                </Threshold>
              );
            })}
          </Scrollable>
        </Thresholds>
        {!!userCollectionClosestToCompletion && (
          <BodyS className="text-center" color="var(--c-nd-600)">
            <FormattedMessage defaultMessage="The club shown is the one closest to completion." />
          </BodyS>
        )}
      </Root>
    </Dialog>
  );
};

CollectionCompletionDetailsDialog.fragments = {
  taskInterface: gql`
    fragment CollectionCompletionDetailsDialog_taskInterface on TaskInterface {
      id
      title
      iconUrl
      ... on CompleteCollectionsCountTask {
        id
        liveScore
        thresholds {
          current
          count
          rewardConfigs {
            id
            ...AnyRewardConfig_anyRewardConfigInterface
          }
        }
        userCollectionClosestToCompletion {
          id
          fulfilledSlotsCount
          slotsCount
          cardCollection {
            slug
            anyTeam {
              slug
              name
              pictureUrl
            }
          }
        }
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<CollectionCompletionDetailsDialog_taskInterface>,
};
